import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Pressable, Text as RNText, Animated } from 'react-native';
import { Text } from './index';
import { Theme } from '../../utils/theme';
import TypingIndicator from './TypingIndicator';
import { formatMarkdown } from '../../utils/simpleMarkdown';

interface ChatBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  isStreaming?: boolean;
  stepLabel?: string; // Optional step label shown above message (e.g., "STEP 1/8")
  isQuestion?: boolean; // Whether this is a question that needs user input
  onPress?: () => void;
  children?: React.ReactNode; // For recommendation pills
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  role,
  content,
  timestamp,
  isStreaming = false,
  stepLabel,
  isQuestion = false,
  onPress,
  children,
}) => {
  const isUser = role === 'user';
  const formattedTime = new Date(timestamp * 1000).toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  // Animation for smooth message appearance
  const scaleY = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Only animate when message has content and is not streaming
    if (content && !isStreaming) {
      Animated.parallel([
        Animated.spring(scaleY, {
          toValue: 1,
          tension: 80,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Reset animation values when streaming
      scaleY.setValue(0);
      opacity.setValue(0);
    }
  }, [content, isStreaming, scaleY, opacity]);

  // Show typing indicator when streaming with no content
  if (isStreaming && !content) {
    return <TypingIndicator dotColor={Theme.colors.textSecondary} size={6} />;
  }

  // Parse content to separate title from subtitle for questions
  // Title and subtitle are separated by \n\n
  const contentParts = isQuestion && content.includes('\n\n') 
    ? content.split('\n\n')
    : null;
  const title = contentParts ? contentParts[0] : null;
  const subtitle = contentParts ? contentParts.slice(1).join('\n\n') : null;
  
  // If it's a question without subtitle, treat whole content as title
  const shouldStyleAsQuestion = isQuestion && !isUser;

  return (
    <Animated.View
      style={{
        transform: [{ scaleY }],
        opacity,
      }}
    >
      <Pressable
        style={[
          styles.bubble,
          isUser ? styles.userBubble : styles.assistantBubble,
        ]}
        onPress={onPress}
        disabled={!onPress}
      >
        {/* Step label (if provided) */}
        {stepLabel && !isUser && (
          <Text 
            variant="caption" 
            style={styles.stepLabel}
          >
            {stepLabel}
          </Text>
        )}
        
        {/* Render content based on whether it's a question */}
        {shouldStyleAsQuestion ? (
          title && subtitle ? (
            // Question with title and subtitle
            <>
              <RNText style={styles.questionTitle}>
                {formatMarkdown(title, styles.questionTitle)}
              </RNText>
              <RNText style={styles.questionSubtitle}>
                {formatMarkdown(subtitle, styles.questionSubtitle)}
              </RNText>
            </>
          ) : (
            // Question without subtitle - just make it bold
            <RNText style={styles.questionTitle}>
              {formatMarkdown(content, styles.questionTitle)}
            </RNText>
          )
        ) : (
          // Regular message
          <RNText 
            style={[
              styles.messageText, 
              isUser ? styles.userMessageText : styles.assistantMessageText
            ].filter(Boolean) as any}
          >
            {formatMarkdown(content, isUser ? styles.userMessageText : styles.assistantMessageText)}
          </RNText>
        )}
        
        {/* Additional content (like recommendation pills or interactive widgets) */}
        {/* Only show widgets when not streaming (text is complete) */}
        {children && !isStreaming && (
          <View style={styles.additionalContent}>
            {children}
          </View>
        )}
        
        {/* Timestamp */}
        <View style={styles.timestampContainer}>
          <Text 
            variant="caption" 
            style={[
              styles.timestamp, 
              isUser ? styles.userTimestamp : undefined
            ].filter(Boolean) as any}
          >
            {formattedTime}
          </Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  bubble: {
    maxWidth: '100%',
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.lg,
    position: 'relative',
  },
  userBubble: {
    backgroundColor: Theme.colors.primary,
    borderBottomRightRadius: Theme.spacing.xs,
  },
  assistantBubble: {
    backgroundColor: Theme.colors.surface,
    borderWidth: 0.3,
    borderColor: 'rgba(0, 0, 0, 0.08)',
    borderBottomLeftRadius: Theme.spacing.xs,
  },
  messageText: {
    lineHeight: 20,
  },
  userMessageText: {
    color: Theme.colors.surface,
  },
  assistantMessageText: {
    color: Theme.colors.textPrimary,
  },
  questionTitle: {
    fontSize: 16,
    lineHeight: 22,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginBottom: Theme.spacing.sm,
  },
  questionSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    opacity: 0.7,
  },
  timestampContainer: {
    alignItems: 'flex-end',
    marginTop: Theme.spacing.xs,
  },
  timestamp: {
    fontSize: 11,
    color: Theme.colors.textSecondary,
    opacity: 0.7,
  },
  userTimestamp: {
    color: 'rgba(255, 255, 255, 0.8)',
  },
  additionalContent: {
    marginTop: Theme.spacing.md,
  },
  stepLabel: {
    fontSize: 11,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: Theme.spacing.xs,
    opacity: 0.8,
  },
});

export default ChatBubble; 